import React, { useEffect, useState } from "react";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import { Table, Form, Spinner, Button } from "react-bootstrap";

export default function ObjectFieldSelector({
  objectName,
  multiSelect = false,
  onSelect,
  fieldTypeFilter = "all", // e.g., 'email', 'lookup', 'email|lookup', 'all'
  excludeReadOnly = false,
  referenceObjectFilter = null, // Filter lookup fields by reference_object (e.g., 'user')
  defaultSelectedFieldName = null, // Pre-selected field name (e.g., 'ownerid')
  selectionUi = "check", // "check" (default) or "button"
  enableSearch = false,
}) {
  const [fields, setFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (objectName) {
      fetchFields(objectName);
    }
  }, [objectName, fieldTypeFilter, excludeReadOnly, referenceObjectFilter]);

  // Set default selected field when fields are loaded or defaultSelectedFieldName changes
  useEffect(() => {
    if (defaultSelectedFieldName && fields.length > 0) {
      const defaultField = fields.find(
        (f) => f.field_name === defaultSelectedFieldName
      );
      if (defaultField) {
        setSelectedFields([defaultField]);
      }
    } else if (!defaultSelectedFieldName && fields.length > 0) {
      // Clear selection if no default field name is provided
      setSelectedFields([]);
    }
  }, [fields, defaultSelectedFieldName]);

  const fetchFields = async (objectName) => {
    setLoading(true);
    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(
        objectName
      );
      console.log("response", response);
      if (response.success && response.data) {
        let filteredFields = response.data.fields || [];

        // Filter by data type (e.g., 'lookup', 'email', etc.)
        if (fieldTypeFilter !== "all") {
          const filters = fieldTypeFilter
            .split("|")
            .map((f) => f.trim().toLowerCase());

          filteredFields = filteredFields.filter((f) => {
            const dataType = f.data_type?.toLowerCase();
            return filters.includes(dataType);
          });
        }

        // Filter by reference object (for lookup fields)
        if (referenceObjectFilter) {
          filteredFields = filteredFields.filter((f) => {
            // Only apply to lookup fields
            if (f.data_type?.toLowerCase() === "lookup" || f.is_reference) {
              const refObject = f.reference_object?.toLowerCase();
              const targetObject = referenceObjectFilter.toLowerCase();
              return refObject === targetObject;
            }
            // For non-lookup fields, keep them if no fieldTypeFilter or if they passed the type filter
            return fieldTypeFilter === "all" || !f.data_type?.toLowerCase().includes("lookup");
          });
        }

        // Exclude read-only fields
        if (excludeReadOnly) {
          filteredFields = filteredFields.filter((f) => !f.is_readonly);
        }

        // Exclude calculated and rollupsummary fields (they cannot be updated)
        filteredFields = filteredFields.filter((f) => {
          const dataType = (f.data_type || '').toLowerCase();
          return dataType !== 'calculated' && dataType !== 'rollupsummary';
        });

        console.log("filteredFields:", filteredFields);
        setFields(filteredFields);
      } else {
        setFields([]);
      }
    } catch (err) {
      console.error("Failed to fetch object fields", err);
      setFields([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSelection = (field) => {
    if (multiSelect) {
      let updatedSelection = [...selectedFields];
      if (updatedSelection.includes(field)) {
        updatedSelection = updatedSelection.filter((f) => f !== field);
      } else {
        updatedSelection.push(field);
      }
      setSelectedFields(updatedSelection);
      onSelect && onSelect(updatedSelection);
    } else {
      setSelectedFields([field]);
      onSelect && onSelect(field);
    }
  };

  if (loading) {
    return <Spinner animation="border" variant="primary" />;
  }

  const q = String(search || "").trim().toLowerCase();
  const visibleFields =
    enableSearch && q
      ? (fields || []).filter((f) => {
          const label = String(f?.field_label || "").toLowerCase();
          const name = String(f?.field_name || "").toLowerCase();
          return label.includes(q) || name.includes(q);
        })
      : fields;

  return (
    <div>
      <h6>Fields for {objectName}</h6>
      {enableSearch && (
        <Form.Control
          className="mb-2"
          size="sm"
          placeholder="Search fields…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <Table bordered size="sm">
        <thead>
          <tr>
            <th style={{ width: selectionUi === "button" ? 90 : undefined }}></th>
            <th>Label</th>
            <th>Field Name</th>

            <th>Merge Field</th>
          </tr>
        </thead>
        <tbody>
          {visibleFields.map((field) => (
            <tr key={field.id}>
              <td>
                {selectionUi === "button" ? (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => handleFieldSelection(field)}
                  >
                    Select
                  </Button>
                ) : (
                  <Form.Check
                    type={multiSelect ? "checkbox" : "radio"}
                    name="fieldSelector"
                    checked={selectedFields.includes(field)}
                    onChange={() => handleFieldSelection(field)}
                  />
                )}
              </td>
              <td>{field.field_label}</td>
              <td>{field.field_name}</td>

              <td>{`{{${field.field_name}}}`}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
